import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import {
  AdoptionAttemptGuestCheckoutDto,
  AdoptionAttemptWithGuestCheckoutDto,
} from '../../interactors/clients/GuestAdoptionsClient'
import { AdoptionAttemptDto } from '../../interactors/gen/backendClient'

// Mirrors `buildGuestCheckoutLink` of the backend: the attempt only carries the token, not the full link.
const ADOPTERS_PLATFORM_DOMAIN = 'https://adoptant.petso.fr'

export const getGuestCheckout = (adoptionAttempt: AdoptionAttemptDto): AdoptionAttemptGuestCheckoutDto | undefined => {
  return (adoptionAttempt as AdoptionAttemptWithGuestCheckoutDto).guestCheckout ?? undefined
}

export const isGuestAdoptionAwaitingPayment = (adoptionAttempt: AdoptionAttemptDto): boolean => {
  const guestCheckout = getGuestCheckout(adoptionAttempt)
  return guestCheckout != null && !guestCheckout.paidAt
}

/** A guest adoption that has been paid and is only waiting for the organization to close it. */
export const isGuestAdoptionPaid = (adoptionAttempt: AdoptionAttemptDto): boolean =>
  Boolean(getGuestCheckout(adoptionAttempt)?.paidAt)

export const getGuestCheckoutUrl = (token: string): string => `${ADOPTERS_PLATFORM_DOMAIN}/adoption/${token}`

export const isGuestCheckoutExpired = (guestCheckout: AdoptionAttemptGuestCheckoutDto): boolean => {
  return !guestCheckout.paidAt && new Date(guestCheckout.expiresAt).getTime() < Date.now()
}

export const formatAmountInCents = (amountInCents: number): string => {
  return `${(amountInCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`
}

export interface GuestAdoptionError {
  /** Message of the backend, to be displayed as is. */
  message: string
  /** The organization has to finish its Stripe Connect onboarding before using the guest adoption. */
  stripeOnboardingRequired: boolean
}

export const getGuestAdoptionError = (error: unknown): GuestAdoptionError => {
  const fallback = { message: 'Une erreur est survenue, veuillez réessayer.', stripeOnboardingRequired: false }

  if (!axios.isAxiosError(error) || !error.response) {
    return fallback
  }

  const backendMessage = (error.response.data as { message?: string | string[] } | undefined)?.message
  const message = Array.isArray(backendMessage) ? backendMessage.join(' ') : backendMessage

  if (!message) {
    return fallback
  }

  return {
    message,
    // The backend has no dedicated error code for this case: it answers 400 with a message naming Stripe.
    stripeOnboardingRequired: error.response.status === StatusCodes.BAD_REQUEST && /stripe/i.test(message),
  }
}
